import {CSSProperties, FC, useEffect, useLayoutEffect, useMemo, useRef, useState} from 'react'
import {createPortal} from 'react-dom'
import './cardInfoGuide.css'

export type CardInfoItem = {
  label: string
  description: string
}

export type CardInfoContent = {
  title: string
  subtitle?: string
  items: CardInfoItem[]
}

type CardInfoGuideProps = {
  content: CardInfoContent
  interaction?: 'click' | 'hover' | 'both'
}

const CardInfoGuide: FC<CardInfoGuideProps> = ({content, interaction = 'click'}) => {
  const [isPopoverOpen, setIsPopoverOpen] = useState(false)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [popoverStyle, setPopoverStyle] = useState<CSSProperties>({})
  const triggerRef = useRef<HTMLButtonElement>(null)
  const closeTimer = useRef<number>()

  const triggerByHover = useMemo(
    () => interaction === 'hover' || interaction === 'both',
    [interaction]
  )
  const triggerByClick = useMemo(
    () => interaction === 'click' || interaction === 'both',
    [interaction]
  )

  const openPopover = () => {
    if (!triggerByHover) return
    window.clearTimeout(closeTimer.current)
    updatePopoverPosition()
    setIsPopoverOpen(true)
  }

  const scheduleClosePopover = () => {
    if (!triggerByHover) return
    window.clearTimeout(closeTimer.current)
    closeTimer.current = window.setTimeout(() => setIsPopoverOpen(false), 180)
  }

  const updatePopoverPosition = () => {
    const trigger = triggerRef.current
    if (!trigger) return

    const rect = trigger.getBoundingClientRect()
    const width = Math.min(360, window.innerWidth - 24)
    const nearSidebar = rect.left < 280
    const fitsRight = window.innerWidth - rect.left >= width + 12
    const openToRight = nearSidebar || fitsRight
    const left = openToRight
      ? Math.min(rect.left, window.innerWidth - width - 12)
      : Math.max(12, rect.right - width)

    setPopoverStyle({
      top: rect.bottom + 8,
      left: Math.max(12, left),
      width,
    })
  }

  useLayoutEffect(() => {
    if (!isPopoverOpen) return
    updatePopoverPosition()
    window.addEventListener('scroll', updatePopoverPosition, true)
    window.addEventListener('resize', updatePopoverPosition)
    return () => {
      window.removeEventListener('scroll', updatePopoverPosition, true)
      window.removeEventListener('resize', updatePopoverPosition)
    }
  }, [isPopoverOpen])

  useEffect(() => {
    return () => window.clearTimeout(closeTimer.current)
  }, [])

  useEffect(() => {
    const handleEsc = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsModalOpen(false)
        setIsPopoverOpen(false)
      }
    }

    if (isModalOpen) {
      document.addEventListener('keydown', handleEsc)
      document.body.style.overflow = 'hidden'
    }

    return () => {
      document.removeEventListener('keydown', handleEsc)
      document.body.style.overflow = ''
    }
  }, [isModalOpen])

  const popover = isPopoverOpen ? (
    <div
      className='card-info-guide__popover'
      style={popoverStyle}
      onMouseEnter={openPopover}
      onMouseLeave={scheduleClosePopover}
    >
      <div className='card-info-guide__popover-title'>{content.title}</div>
      {content.subtitle && (
        <div className='card-info-guide__popover-subtitle'>{content.subtitle}</div>
      )}
      <ul className='card-info-guide__popover-list'>
        {content.items.slice(0, 3).map((item) => (
          <li key={item.label}>
            <strong>{item.label}:</strong> {item.description}
          </li>
        ))}
      </ul>
      {content.items.length > 3 && (
        <div className='card-info-guide__popover-footer'>Clique para ver o guia completo</div>
      )}
    </div>
  ) : null

  return (
    <>
      <div
        className='card-info-guide'
        onMouseEnter={openPopover}
        onMouseLeave={scheduleClosePopover}
      >
        <button
          type='button'
          ref={triggerRef}
          className='btn btn-sm btn-icon card-info-guide__trigger'
          aria-label='Ajuda deste card'
          onClick={() => triggerByClick && setIsModalOpen(true)}
        >
          <i className='bi bi-info-circle'></i>
        </button>
      </div>
      {popover && createPortal(popover, document.body)}

      {isModalOpen && (
        <div className='card-info-guide__modal-backdrop' onClick={() => setIsModalOpen(false)}>
          <div className='card-info-guide__modal' onClick={(e) => e.stopPropagation()}>
            <div className='card-info-guide__modal-header'>
              <div>
                <h4 className='card-info-guide__modal-title mb-0'>{content.title}</h4>
                {content.subtitle && (
                  <div className='card-info-guide__modal-subtitle mt-1'>{content.subtitle}</div>
                )}
              </div>
              <button
                type='button'
                className='btn btn-sm btn-icon btn-light card-info-guide__close'
                onClick={() => setIsModalOpen(false)}
                aria-label='Fechar guia'
              >
                <i className='bi bi-x-lg'></i>
              </button>
            </div>

            <div className='card-info-guide__modal-body'>
              <ul className='card-info-guide__modal-list'>
                {content.items.map((item) => (
                  <li key={item.label} className='card-info-guide__modal-item'>
                    <div className='card-info-guide__item-label'>{item.label}</div>
                    <div className='card-info-guide__item-description'>{item.description}</div>
                  </li>
                ))}
              </ul>
            </div>

            <div className='card-info-guide__modal-footer'>
              <button
                type='button'
                className='btn btn-light-primary fw-semibold'
                onClick={() => setIsModalOpen(false)}
              >
                Entendi
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  )
}

export {CardInfoGuide}
